import api from '../api';

export interface GlobalWorkLogData {
  title: string;
  startDate: string;
  endDate: string;
  organizationName?: string;
  managementCompany?: string;
  dutyStaff?: string;
  staffAttendance?: any;
  equipmentStatus?: any;
  otherWorkItems?: any;
}

export interface GlobalWorkLogUpdateData extends Partial<GlobalWorkLogData> {
  status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
}

// 獲取用戶可存取的專案列表
export const getAccessibleProjects = async () => {
  const response = await api.get('/global-worklogs/accessible-projects');
  return response.data;
};

// 獲取全域工作日誌列表
export const getGlobalWorkLogs = async (status?: string) => {
  const params = status ? { status } : {};
  const response = await api.get('/global-worklogs', { params });
  return response.data;
};

// 獲取單個全域工作日誌
export const getGlobalWorkLog = async (id: string) => {
  const response = await api.get(`/global-worklogs/${id}`);
  return response.data;
};

// 創建全域工作日誌
export const createGlobalWorkLog = async (data: GlobalWorkLogData) => {
  const response = await api.post('/global-worklogs', data);
  return response.data;
};

// 更新全域工作日誌
export const updateGlobalWorkLog = async (id: string, data: GlobalWorkLogUpdateData) => {
  const response = await api.put(`/global-worklogs/${id}`, data);
  return response.data;
};

// 刪除全域工作日誌
export const deleteGlobalWorkLog = async (id: string) => {
  const response = await api.delete(`/global-worklogs/${id}`);
  return response.data;
};

// 為全域工作日誌添加議題
export const addIssuesToGlobalWorkLog = async (id: string, issueIds: string[]) => {
  const response = await api.post(`/global-worklogs/${id}/issues`, { issueIds });
  return response.data;
};

// 從全域工作日誌的預設分組移除議題
export const removeIssueFromGlobalWorkLog = async (workLogId: string, issueId: string) => {
  const response = await api.delete(`/global-worklogs/${workLogId}/issues/${issueId}`);
  return response.data;
};

// 更新全域工作日誌中議題的順序
export const updateGlobalWorkLogIssueOrder = async (workLogId: string, issueOrders: Array<{ issueId: string; sortOrder: number }>) => {
  const response = await api.put(`/global-worklogs/${workLogId}/issues/order`, { issueOrders });
  return response.data;
};

// 創建自定義議題分組
export const createIssueGroup = async (workLogId: string, data: { title: string; issueIds: string[] }) => {
  const response = await api.post(`/global-worklogs/${workLogId}/issue-groups`, data);
  return response.data;
};

// 更新議題分組標題
export const updateIssueGroup = async (workLogId: string, groupId: string, data: { title: string }) => {
  const response = await api.put(`/global-worklogs/${workLogId}/issue-groups/${groupId}`, data);
  return response.data;
};

// 同步議題分組的議題列表
export const syncIssueGroupIssues = async (workLogId: string, groupId: string, issueIds: string[]) => {
  const response = await api.put(`/global-worklogs/${workLogId}/issue-groups/${groupId}/sync-issues`, { issueIds });
  return response.data;
};

// 刪除議題分組
export const deleteIssueGroup = async (workLogId: string, groupId: string) => {
  const response = await api.delete(`/global-worklogs/${workLogId}/issue-groups/${groupId}`);
  return response.data;
};

// 建立公開分享連結（生成靜態文件）
export const createPublicShare = async (workLogId: string) => {
  const response = await api.post(`/global-worklogs/${workLogId}/share/public`);
  return response.data;
};

// 建立私人分享連結（返回當前頁面 URL）
export const createPrivateShare = async (workLogId: string) => {
  const response = await api.post(`/global-worklogs/${workLogId}/share/private`);
  return response.data;
};

// 同步工作日誌的議題列表（添加新的，移除取消勾選的）- 僅用於預設分組
export const syncWorkLogIssues = async (workLogId: string, newIssueIds: string[], currentIssueIds: string[]) => {
  const newIssueIdsSet = new Set(newIssueIds);
  const currentIssueIdsSet = new Set(currentIssueIds);
  
  // 找出需要添加的議題（新選的但不在當前列表中）
  const toAdd = newIssueIds.filter(id => !currentIssueIdsSet.has(id));
  
  // 找出需要移除的議題（當前列表中但未選中）
  const toRemove = currentIssueIds.filter(id => !newIssueIdsSet.has(id));
  
  const results = [];
  
  // 添加新議題（預設分組，groupId 為 null）
  if (toAdd.length > 0) {
    const addResult = await addIssuesToGlobalWorkLog(workLogId, toAdd);
    results.push({ action: 'add', count: toAdd.length, result: addResult });
  }
  
  // 移除議題（只移除預設分組的議題）
  for (const issueId of toRemove) {
    try {
      const removeResult = await removeIssueFromGlobalWorkLog(workLogId, issueId);
      results.push({ action: 'remove', issueId, result: removeResult });
    } catch (error) {
      console.error(`Failed to remove issue ${issueId}:`, error);
    }
  }
  
  return {
    added: toAdd.length,
    removed: toRemove.length,
    results
  };
};

// 天氣相關 API
export const getWeatherData = async (location: string, date: string) => {
  const response = await api.get('/global-worklogs/weather/data', {
    params: { location, date }
  });
  return response.data;
};

export const getWeatherSummary = async (location: string, startDate: string, endDate: string) => {
  const response = await api.get('/global-worklogs/weather/summary', {
    params: { location, startDate, endDate }
  });
  return response.data;
};

export const getTaiwanCities = async () => {
  const response = await api.get('/global-worklogs/weather/cities');
  return response.data;
};